// Figma: "Invoices / Download / Success" and "Invoices / Download / Error" (nodes
// 18702:2287 and 18702:2612, both a "Toast" component instance) — title + description,
// a close button, and a colored progress bar along the bottom edge that shrinks to signal
// the auto-dismiss countdown (green for success, red for error, per the component's own
// `--ui-success`/`--ui-error` variables).
import React, {useEffect} from 'react';
import PropTypes from 'prop-types';
import {Box, ButtonBase, Stack, Typography} from '@mui/material';
import {keyframes} from '@mui/material/styles';
import {tokens} from '../../../themes/ThemeCustomization';

import toastCloseIcon from '../../../assets/shell/toast-close-icon.svg';

const TOAST_DURATION_MS = 5000;

const shrink = keyframes({from: {transform: 'scaleX(1)'}, to: {transform: 'scaleX(0)'}});

const InvoiceToast = ({toast, onClose}) => {
    useEffect(() => {
        if (!toast) return undefined;
        const timer = setTimeout(onClose, TOAST_DURATION_MS);
        return () => clearTimeout(timer);
    }, [toast, onClose]);

    if (!toast) return null;

    const isSuccess = toast.type === 'success';

    return (
        <Box sx={{position: 'fixed', bottom: 24, right: 24, width: 384, zIndex: 1400}}>
            <Stack
                direction="row"
                alignItems="flex-start"
                justifyContent="space-between"
                sx={{
                    position: 'relative',
                    backgroundColor: tokens.color.white,
                    border: `1px solid ${tokens.color.neutral200}`,
                    borderRadius: tokens.radius.toast,
                    boxShadow: tokens.shadow.toast,
                    p: 2,
                    overflow: 'hidden'
                }}
            >
                <Stack spacing={0.5}>
                    <Typography variant="body2" fontWeight={500}>
                        {toast.title}
                    </Typography>
                    <Typography variant="body2" sx={{color: tokens.color.neutral500}}>
                        {toast.description}
                    </Typography>
                </Stack>
                <ButtonBase onClick={onClose} sx={{p: 0.5, borderRadius: '6px'}}>
                    <Box component="img" src={toastCloseIcon} alt="Dismiss" sx={{width: 16, height: 16}} />
                </ButtonBase>
                <Box
                    sx={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        height: '4px',
                        transformOrigin: 'left',
                        backgroundColor: isSuccess ? tokens.color.success500 : tokens.color.error500,
                        animation: `${shrink} ${TOAST_DURATION_MS}ms linear forwards`
                    }}
                />
            </Stack>
        </Box>
    );
};

InvoiceToast.propTypes = {
    toast: PropTypes.shape({
        type: PropTypes.oneOf(['success', 'error']).isRequired,
        title: PropTypes.string.isRequired,
        description: PropTypes.string.isRequired
    }),
    onClose: PropTypes.func.isRequired
};

export default InvoiceToast;
